/**
 * DashboardPage — the workspace's home surface.
 *
 * Composition only. Renders a workspace-branded hero, the CapabilityGrid,
 * and a members side panel. Business logic lives in ``entities/workspace``.
 */
import { useCapabilityMatrix, useCurrentWorkspace, useMembers } from "@/entities/workspace";
import { CampaignRunSummaryCard } from "@/widgets/CampaignRunSummary/CampaignRunSummaryCard";
import { CapabilityGrid } from "@/widgets/CapabilityGrid";
import { WorkspaceMonogram } from "@/entities/workspace";
import { Badge } from "@/shared/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";

export function DashboardPage(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const matrix = useCapabilityMatrix(workspace?.id ?? null);
  const members = useMembers(workspace?.id ?? null);

  if (!workspace) {
    return <EmptyWorkspaceState />;
  }

  const primary = workspace.profile.primary_color;

  return (
    <div className="relative min-h-[calc(100vh-8.5rem)] overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-10 bg-grid opacity-30" />
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 -z-10 h-72"
        style={{
          background: `linear-gradient(to bottom, ${primary}18, transparent)`,
        }}
      />

      <div className="mx-auto max-w-7xl px-6 py-12 md:py-16">
        {/* Hero */}
        <div
          data-testid="workspace-hero"
          className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
        >
          <div className="flex items-center gap-5">
            <WorkspaceMonogram workspace={workspace} size="lg" />
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/60 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: primary }}
                />
                {workspace.slug}
              </div>
              <h1
                data-testid="workspace-hero-title"
                className="font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl"
              >
                {workspace.profile.display_name}
              </h1>
              {workspace.profile.tagline ? (
                <p className="max-w-2xl text-base text-muted-foreground md:text-lg">
                  {workspace.profile.tagline}
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {workspace.profile.industry ? (
              <Badge variant="outline" className="font-normal">
                {workspace.profile.industry}
              </Badge>
            ) : null}
            <Badge variant="outline" className="font-mono uppercase tracking-widest text-[10px]">
              {workspace.profile.timezone}
            </Badge>
            <Badge variant="outline" className="font-mono uppercase tracking-widest text-[10px]">
              {workspace.profile.currency}
            </Badge>
          </div>
        </div>

        <div className="mt-10">
          {/* Patch 34.2.1 — canonical CampaignRun counters, source of truth
              for every Dashboard-level campaign number. */}
          <CampaignRunSummaryCard workspaceId={workspace.id} />
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <CapabilityGrid
            workspace={workspace}
            matrix={matrix.data}
            isLoading={matrix.isLoading}
          />
          <aside className="space-y-6">
            <Card data-testid="members-card" className="border-border/70 bg-card/70 backdrop-blur">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">
                  Team
                </CardTitle>
                <CardDescription>
                  Live capability · workspace members and roles.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {members.isLoading ? (
                  <>
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </>
                ) : (
                  (members.data ?? []).map((m) => (
                    <div
                      key={m.id}
                      data-testid={`member-row-${m.user_id}`}
                      className="flex items-center justify-between rounded-md border border-border/60 bg-background/50 px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium">{m.user_id.slice(0, 8)}…</p>
                        <p className="text-[11px] text-muted-foreground">
                          Since {new Date(m.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge variant="secondary" className="uppercase text-[10px]">
                        {m.role}
                      </Badge>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card
              data-testid="settings-card"
              className="border-border/70 bg-card/70 backdrop-blur"
            >
              <CardHeader>
                <CardTitle className="text-lg font-semibold">
                  Settings
                </CardTitle>
                <CardDescription>Live capability.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <SettingsRow label="Email notifications">
                  {workspace.settings.notifications_email ? "On" : "Off"}
                </SettingsRow>
                <SettingsRow label="Default AI provider">
                  <code className="font-mono text-xs">{workspace.settings.default_ai_provider}</code>
                </SettingsRow>
                <SettingsRow label="Content retention">
                  {workspace.settings.content_retention_days} days
                </SettingsRow>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
}

function SettingsRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between border-b border-border/50 py-1.5 last:border-0">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <span className="text-sm">{children}</span>
    </div>
  );
}

function EmptyWorkspaceState(): JSX.Element {
  return (
    <div
      data-testid="dashboard-empty-state"
      className="mx-auto flex min-h-[calc(100vh-8.5rem)] max-w-2xl flex-col items-center justify-center gap-4 px-6 text-center"
    >
      <h1 className="font-display text-3xl font-semibold">No workspace selected</h1>
      <p className="text-sm text-muted-foreground">
        Pick a workspace from the switcher to see its capability grid, or create a new one.
      </p>
    </div>
  );
}
